import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check, MoreVertical } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '../ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { FOCUSABLE_SELECTOR, nextFocusable, previousFocusable } from '../../lib/focus-order';
import { cn } from '../../lib/utils';

export interface MenuAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  destructive?: boolean;
  /** Renders a check next to the label — for actions that toggle a state. */
  checked?: boolean;
  /**
   * Color class for the icon. Lets a menu entry carry the same color its
   * subject wears elsewhere, so picking a state from the menu and recognizing
   * it in the list are the same visual vocabulary.
   */
  iconClassName?: string;
}

export interface MenuActionSeparator {
  separator: true;
}

/**
 * A nested group of related actions. Use it when a row gains a whole family of
 * options (say, the triage marks) that would otherwise flood the top level and
 * bury the row's everyday actions.
 */
export interface MenuActionSubmenu {
  label: string;
  icon: LucideIcon;
  items: MenuAction[];
  /** Color class for the trigger icon; see {@link MenuAction.iconClassName}. */
  iconClassName?: string;
}

export type MenuActionItem = MenuAction | MenuActionSeparator | MenuActionSubmenu;

/**
 * Marks a row that owns a portaled action toolbar. Rows find their neighbours
 * through the DOM rather than a module-level registry, so a registration can
 * never outlive the node it points at — or bleed between two trees rendered in
 * the same module scope, as Storybook does.
 */
const SIDEBAR_ITEM_ATTR = 'data-sidebar-item';
const ROW_MARKER = { [SIDEBAR_ITEM_ATTR]: '' };
/** Marks a portaled toolbar so rows can step over one another's controls. */
const SIDEBAR_ITEM_ACTIONS_ATTR = 'data-sidebar-item-actions';
const ACTION_SURFACE_MARKER = { [SIDEBAR_ITEM_ACTIONS_ATTR]: '' };
/**
 * Asks a row to focus the last control of its toolbar, opening it if needed.
 * Dispatched on the row element by the row that follows it, so reverse-tabbing
 * out of a row lands on the previous row's actions.
 */
const FOCUS_TOOLBAR_END_EVENT = 'reflex:sidebar-item-focus-toolbar-end';

function isSeparator(item: MenuActionItem): item is MenuActionSeparator {
  return 'separator' in item;
}

function isSubmenu(item: MenuActionItem): item is MenuActionSubmenu {
  return 'items' in item;
}

interface SidebarItemProps {
  selected?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
  /** Quick-action icon buttons rendered after the MoreVertical trigger (rightmost). */
  actions?: React.ReactNode;
  /** DropdownMenu items rendered inside a MoreVertical dropdown. */
  menuItems?: React.ReactNode;
  /** ContextMenu items. Falls back to `menuItems` when omitted. */
  contextMenuItems?: React.ReactNode;
  /** Declarative menu actions rendered in both dropdown and context menus. Takes precedence over menuItems/contextMenuItems. */
  menuActions?: MenuActionItem[];
  /** Extra background class for highlight states (e.g. waiting_input amber tint). */
  highlight?: string;
  /** Reduce opacity for de-emphasized items (e.g. archived). */
  dimmed?: boolean;
  className?: string;
  /** Optional data-testid applied to the outer row element for use in tests. */
  testId?: string;
  /** Row participates in multi-select. Only takes effect while `selectionMode` is on. */
  selectable?: boolean;
  /** Multi-select mode is active: `selectable` rows show a checkbox in place of their icon and clicking toggles selection instead of firing `onClick`. */
  selectionMode?: boolean;
  /** Whether this row is currently checked in the selection. Distinct from `selected` (the open/active row). */
  checked?: boolean;
  /** Toggle this row's selection. `shiftKey` reflects the originating event so callers can implement range selection. */
  onToggleSelect?: (shiftKey: boolean) => void;
}

/** Presentational selection checkbox rendered in the leading slot during select mode. */
function SelectionCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex h-4 w-4 items-center justify-center rounded-[4px] border transition-colors',
        checked
          ? 'border-ring bg-ring text-background'
          : 'border-muted-foreground/50 bg-transparent',
      )}
    >
      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
  );
}

function countChildren(node: React.ReactNode): number {
  let count = 0;
  Children.forEach(node, (child) => {
    if (isValidElement<{ children?: React.ReactNode }>(child) && child.type === Fragment) {
      count += countChildren(child.props.children);
    } else {
      count += 1;
    }
  });
  return count;
}

export function SidebarItem({
  selected = false,
  onClick,
  icon,
  children,
  actions,
  menuItems,
  contextMenuItems,
  menuActions,
  highlight,
  dimmed,
  className,
  testId,
  selectable = false,
  selectionMode = false,
  checked = false,
  onToggleSelect,
}: SidebarItemProps) {
  const isDesktop = useIsDesktop();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const actionSurfaceRef = useRef<HTMLDivElement | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const openActions = useCallback(() => {
    cancelClose();
    setActionsOpen(true);
  }, [cancelClose]);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setActionsOpen(false);
      closeTimer.current = null;
    }, 100);
  };

  // Menu actions (archive, delete) often unmount this row. If they run in the
  // same tick as the click, the portaled overlay is left without a Root to
  // close it and freezes on screen. Close every overlay first, then run the
  // action on the next microtask.
  const runAfterMenuClose = useCallback(
    (action: () => void) => {
      cancelClose();
      setMenuOpen(false);
      setActionsOpen(false);
      queueMicrotask(action);
    },
    [cancelClose],
  );

  useEffect(
    () => () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    },
    [],
  );

  // Selecting: this row is an active checkbox target. Its click toggles
  // selection rather than navigating, and its quick-actions are suppressed so
  // rows stay calm while the user is picking a set.
  const selecting = selectionMode && selectable;
  // A shift-click on a selectable row always toggles selection, even before
  // select mode is on — the entry gesture that turns select mode on. Once in
  // select mode, a plain click toggles too.
  const handleActivate = (shiftKey: boolean) => {
    if (selectable && (selecting || shiftKey)) onToggleSelect?.(shiftKey);
    else onClick();
  };

  const derivedMenuItems = menuActions ? (
    <>
      {menuActions.map((action, i) =>
        isSeparator(action) ? (
          <DropdownMenuSeparator key={i} />
        ) : isSubmenu(action) ? (
          <DropdownMenuSub key={action.label}>
            <DropdownMenuSubTrigger>
              <action.icon className={cn('icon-md', action.iconClassName)} />
              {action.label}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {action.items.map((item) => (
                <DropdownMenuItem key={item.label} onSelect={() => runAfterMenuClose(item.onClick)}>
                  <item.icon className={cn('icon-md', item.iconClassName)} />
                  {item.label}
                  {item.checked && <Check className="ml-auto icon-md" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuItem
            key={action.label}
            className={action.destructive ? 'text-destructive focus:text-destructive' : undefined}
            onSelect={() => runAfterMenuClose(action.onClick)}
          >
            <action.icon className={cn('icon-md', action.iconClassName)} />
            {action.label}
            {action.checked && <Check className="ml-auto icon-md" />}
          </DropdownMenuItem>
        ),
      )}
    </>
  ) : (
    menuItems
  );

  const derivedContextItems = menuActions ? (
    <>
      {menuActions.map((action, i) =>
        isSeparator(action) ? (
          <ContextMenuSeparator key={i} />
        ) : isSubmenu(action) ? (
          <ContextMenuSub key={action.label}>
            <ContextMenuSubTrigger>
              <action.icon className={cn('icon-md', action.iconClassName)} />
              {action.label}
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {action.items.map((item) => (
                <ContextMenuItem key={item.label} onSelect={() => runAfterMenuClose(item.onClick)}>
                  <item.icon className={cn('icon-md', item.iconClassName)} />
                  {item.label}
                  {item.checked && <Check className="ml-auto icon-md" />}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        ) : (
          <ContextMenuItem
            key={action.label}
            className={action.destructive ? 'text-destructive focus:text-destructive' : undefined}
            onSelect={() => runAfterMenuClose(action.onClick)}
          >
            <action.icon className={cn('icon-md', action.iconClassName)} />
            {action.label}
            {action.checked && <Check className="ml-auto icon-md" />}
          </ContextMenuItem>
        ),
      )}
    </>
  ) : (
    (contextMenuItems ?? menuItems)
  );

  const hasMenu = !!derivedMenuItems && countChildren(derivedMenuItems) > 1;
  // Hide the hover quick-actions/menu while selecting so the row reads as a
  // single toggle target.
  const hasActions = (!!actions || hasMenu) && !selecting;
  const hasContext = !!derivedContextItems && !selecting;

  const toolbarControls = useCallback(
    () =>
      Array.from(actionSurfaceRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []),
    [],
  );

  const rowControls = () =>
    Array.from(rowRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []);

  const focusToolbarControl = useCallback(
    (edge: 'first' | 'last') => {
      openActions();
      requestAnimationFrame(() => {
        const controls = toolbarControls();
        controls[edge === 'first' ? 0 : controls.length - 1]?.focus();
      });
    },
    [openActions, toolbarControls],
  );

  useEffect(() => {
    const row = rowRef.current;
    if (!row || !isDesktop || !hasActions) return;
    const focusToolbarEnd = () => focusToolbarControl('last');
    row.addEventListener(FOCUS_TOOLBAR_END_EVENT, focusToolbarEnd);
    return () => row.removeEventListener(FOCUS_TOOLBAR_END_EVENT, focusToolbarEnd);
  }, [isDesktop, hasActions, focusToolbarControl]);

  // Controls inside a portaled toolbar — ours, which focus is leaving, or a
  // neighbour's — sit outside the row's in-flow tab order.
  const inAToolbar = (element: HTMLElement) => !!element.closest(`[${SIDEBAR_ITEM_ACTIONS_ATTR}]`);

  /**
   * Reverse-tabbing off a row bridges into the previous row's toolbar, but only
   * when that row is also the immediately preceding in-flow focus stop. Section
   * headers and their actions belong between groups and keep their native order.
   */
  const focusPreviousRowToolbarEnd = () => {
    const row = rowRef.current;
    if (!row) return false;
    const previous = previousFocusable(row, inAToolbar);
    const previousRow = previous?.closest<HTMLElement>(`[${SIDEBAR_ITEM_ATTR}]`);
    if (!previousRow) return false;
    previousRow.dispatchEvent(new CustomEvent(FOCUS_TOOLBAR_END_EVENT));
    return true;
  };

  const focusAfterRow = () => {
    const row = rowRef.current;
    if (!row) return false;
    const next = nextFocusable(row, (element) => row.contains(element) || inAToolbar(element));
    next?.focus();
    return !!next;
  };

  const actionToolbar = hasActions && (
    <div
      role="toolbar"
      aria-label="Item actions"
      className="flex items-center gap-0.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (!isDesktop || e.key !== 'Tab') return;
        const controls = toolbarControls();
        if (e.shiftKey && e.target === controls[0]) {
          e.preventDefault();
          const controlsInRow = rowControls();
          (controlsInRow[controlsInRow.length - 1] ?? rowRef.current)?.focus();
        } else if (!e.shiftKey && e.target === controls[controls.length - 1] && focusAfterRow()) {
          e.preventDefault();
        }
      }}
    >
      {hasMenu && (
        <DropdownMenu
          open={menuOpen}
          onOpenChange={(open) => {
            setMenuOpen(open);
            if (open) openActions();
            else scheduleClose();
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="More actions"
                  className="p-1 rounded hover:text-foreground"
                >
                  <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">More</TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="start" onClick={(e) => e.stopPropagation()}>
            {derivedMenuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {actions}
    </div>
  );

  // Touch layouts keep the actions inline and always visible. On desktop the
  // toolbar is portaled outside the sidebar so it never covers the row's chips
  // or gets clipped by the sidebar's overflow boundary.
  const inlineActionSlot = !isDesktop && hasActions && (
    <div
      className={cn(
        // `inset-y-0` rather than a fixed height: a row that grows (a two-line
        // title, say) keeps its actions centred against the taller row.
        'pointer-events-none absolute inset-y-0 right-0 flex shrink-0 items-center gap-0.5 pl-16 pr-1',
        selected
          ? 'bg-gradient-to-r from-selected/0 via-selected/80 to-selected'
          : 'bg-gradient-to-r from-transparent via-background/90 to-background',
      )}
    >
      <div className="pointer-events-auto">{actionToolbar}</div>
    </div>
  );

  const itemClasses = cn(
    'group relative flex w-full items-start gap-2.5 px-3 py-2 text-left transition-all duration-150 cursor-pointer',
    // The gradient only softens toward the trailing edge; it must not fade far
    // enough for the tail of the row to read as unselected.
    selected
      ? 'bg-gradient-to-r from-selected to-selected/70 border-l-2 border-selected-border text-selected-foreground'
      : (highlight ??
          'text-sidebar-foreground hover:bg-sidebar-accent/30 border-l-2 border-transparent'),
    // While selecting, checked rows get a subtle tint so a multi-selection
    // reads at a glance even for rows that are not the open one. Deliberately
    // lighter than the open row's tint so the two stay tellable apart.
    selecting && checked && 'bg-selected/45',
    dimmed && 'opacity-60',
    className,
  );

  // `h-5` matches the title's line box, so the glyph centres on the first line
  // of a multi-line row rather than on the row as a whole. `icon-optical-center`
  // then drops it the ~1px from the cap-height centre to the x-height centre,
  // where a sentence-case label actually carries its weight. The nudge sits on
  // the slot, not the icon, so the checkbox that replaces the icon in select
  // mode lands at the same height and the column does not jump.
  const leadingSlot = (
    <span className="flex h-5 items-center shrink-0 icon-optical-center">
      {selecting ? <SelectionCheckbox checked={checked} /> : icon}
    </span>
  );

  const selectA11y = selecting ? { role: 'checkbox' as const, 'aria-checked': checked } : undefined;
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDesktop && hasActions && actionsOpen && e.key === 'Tab') {
      const controlsInRow = rowControls();
      if (e.shiftKey && e.target === e.currentTarget) {
        if (focusPreviousRowToolbarEnd()) {
          e.preventDefault();
          return;
        }
      } else if (
        !e.shiftKey &&
        (controlsInRow.length === 0
          ? e.target === e.currentTarget
          : e.target === controlsInRow[controlsInRow.length - 1])
      ) {
        const firstControl = toolbarControls()[0];
        if (!firstControl) return;
        e.preventDefault();
        firstControl.focus();
        return;
      }
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e.shiftKey);
    }
  };
  // Shift-click is the selection gesture; stop the browser from starting a text
  // range selection off the shift+mousedown so the row label doesn't highlight.
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.shiftKey && selectable) e.preventDefault();
  };

  const hoverAndFocusProps =
    isDesktop && hasActions
      ? {
          onPointerEnter: openActions,
          onPointerLeave: () => {
            if (!menuOpen) scheduleClose();
          },
          onFocus: openActions,
          onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
            const next = e.relatedTarget;
            if (
              next instanceof Node &&
              (rowRef.current?.contains(next) || actionSurfaceRef.current?.contains(next))
            ) {
              return;
            }
            if (!menuOpen) scheduleClose();
          },
        }
      : undefined;

  // Only a row that owns a portaled toolbar is a bridge target for the next
  // row's reverse-tab, so only those rows advertise themselves.
  const rowMarker = isDesktop && hasActions ? ROW_MARKER : undefined;

  const rowContents = (
    <>
      {leadingSlot}
      <div className="w-0 flex-1 overflow-hidden">{children}</div>
      {inlineActionSlot}
    </>
  );

  const contextTrigger = (
    <ContextMenuTrigger
      ref={rowRef}
      tabIndex={0}
      {...rowMarker}
      {...selectA11y}
      {...hoverAndFocusProps}
      onMouseDown={handleMouseDown}
      onClick={(e) => handleActivate(e.shiftKey)}
      onKeyDown={handleKeyDown}
      className={itemClasses}
      data-testid={testId}
      data-selected={selected ? 'true' : undefined}
      data-checked={selecting && checked ? 'true' : undefined}
    >
      {rowContents}
    </ContextMenuTrigger>
  );

  const plainRow = (
    <div
      ref={rowRef}
      tabIndex={0}
      {...rowMarker}
      {...selectA11y}
      {...hoverAndFocusProps}
      onMouseDown={handleMouseDown}
      onClick={(e) => handleActivate(e.shiftKey)}
      onKeyDown={handleKeyDown}
      className={itemClasses}
      data-testid={testId}
      data-selected={selected ? 'true' : undefined}
      data-checked={selecting && checked ? 'true' : undefined}
    >
      {rowContents}
    </div>
  );

  const row = hasContext ? (
    <ContextMenu>
      {isDesktop && hasActions ? (
        <PopoverAnchor asChild>{contextTrigger}</PopoverAnchor>
      ) : (
        contextTrigger
      )}
      <ContextMenuContent>{derivedContextItems}</ContextMenuContent>
    </ContextMenu>
  ) : isDesktop && hasActions ? (
    <PopoverAnchor asChild>{plainRow}</PopoverAnchor>
  ) : (
    plainRow
  );

  if (!isDesktop || !hasActions) return row;

  return (
    <Popover
      open={actionsOpen}
      onOpenChange={(open) => {
        if (open) openActions();
        else {
          cancelClose();
          setActionsOpen(false);
        }
      }}
    >
      {row}
      <PopoverContent
        ref={actionSurfaceRef}
        {...ACTION_SURFACE_MARKER}
        aria-label="Item action controls"
        side="right"
        align="center"
        sideOffset={0}
        collisionPadding={8}
        className="w-auto p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={openActions}
        onPointerLeave={() => {
          if (!menuOpen) scheduleClose();
        }}
        onFocus={openActions}
        onBlur={(e) => {
          const next = e.relatedTarget;
          if (
            next instanceof Node &&
            (rowRef.current?.contains(next) || actionSurfaceRef.current?.contains(next))
          ) {
            return;
          }
          if (!menuOpen) scheduleClose();
        }}
        onFocusOutside={(e) => e.preventDefault()}
        // The More dropdown renders in its own portal, so opening it reads as an
        // interaction outside this popover. The toolbar owns that menu and must
        // outlive it.
        onInteractOutside={(e) => {
          if (menuOpen) e.preventDefault();
        }}
      >
        {actionToolbar}
      </PopoverContent>
    </Popover>
  );
}
